<template>
  <div class="listing-detail-root">
    <div id="layout-wrapper" class="ld-layout-wrapper">

      <!-- ========== TOP BAR ========== -->
      <nav class="ld-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:16px;">
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" />
            </a>
            <button class="btn btn-sm btn-link text-muted p-0" @click="$router.back()">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="ld-nav-link">Marketplace</a>
            <a href="javascript:void(0)" @click="$router.push('/marketplace/search')" class="ld-nav-link">Search</a>
            <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-sm btn-outline-warning">
              <i class="mdi mdi-plus me-1"></i> Sell
            </a>
            <a href="javascript:void(0)" v-if="myAvatar || myName" @click="$router.push('/me')" title="My profile">
              <user-avatar :src="myAvatar" :name="myName" size="sm" />
            </a>
          </div>
        </div>
      </nav>

      <!-- ========== LOADING ========== -->
      <div v-if="loading" class="text-center py-5" style="margin-top:80px;">
        <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
        <p class="mt-2 text-muted">Loading listing details...</p>
      </div>

      <!-- ========== NOT FOUND ========== -->
      <div v-else-if="!listing.id" class="text-center py-5" style="margin-top:80px;">
        <i class="mdi mdi-alert-circle-outline" style="font-size:64px;color:var(--fm-m,#A09888);"></i>
        <h4 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Listing Not Found</h4>
        <p class="text-muted">This listing may have been removed or is no longer available.</p>
        <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="btn btn-warning text-dark fw-bold">
          <i class="mdi mdi-store me-1"></i> Browse Marketplace
        </a>
      </div>

      <!-- ========== MAIN CONTENT ========== -->
      <div v-else class="container py-4" style="margin-top:10px;">

        <!-- Breadcrumb -->
        <nav class="mb-3">
          <ol class="breadcrumb ld-breadcrumb">
            <li class="breadcrumb-item"><a href="javascript:void(0)" @click="$router.push('/marketplace')">Marketplace</a></li>
            <li class="breadcrumb-item" v-if="listing.species_name"><a href="javascript:void(0)" @click="$router.push('/marketplace/category/' + (listing.species_slug || ''))">{{ listing.species_name }}</a></li>
            <li class="breadcrumb-item active">{{ truncate(listing.title, 40) }}</li>
          </ol>
        </nav>

        <div class="row">

          <!-- ===== LEFT: PHOTOS & DETAILS ===== -->
          <div class="col-lg-8">

            <!-- Photo Gallery -->
            <div class="ld-gallery mb-4">
              <div class="ld-main-photo">
                <img v-if="activePhoto" :src="activePhoto" :alt="listing.title" class="ld-main-img" />
                <div v-else class="ld-main-placeholder">
                  <i :class="getSpeciesIcon(listing.species_name)" style="font-size:80px;color:rgba(191,161,100,0.25);"></i>
                  <p style="color:var(--fm-m,#A09888);margin-top:8px;font-size:14px;">No photos available</p>
                </div>
                <!-- Photo nav arrows -->
                <button v-if="photos.length > 1" class="ld-photo-nav ld-photo-prev" @click="prevPhoto"><i class="mdi mdi-chevron-left"></i></button>
                <button v-if="photos.length > 1" class="ld-photo-nav ld-photo-next" @click="nextPhoto"><i class="mdi mdi-chevron-right"></i></button>
                <!-- Photo counter -->
                <div v-if="photos.length > 1" class="ld-photo-counter">{{ activePhotoIndex + 1 }} / {{ photos.length }}</div>
                <!-- Badges -->
                <div class="ld-photo-badges">
                  <span v-if="listing.animal_id" class="badge" style="background:rgba(80,200,120,.9);color:#fff;"><i class="mdi mdi-barn me-1"></i>Farm Records</span>
                  <span v-if="listing.is_verified" class="badge bg-success"><i class="mdi mdi-check-decagram me-1"></i>Verified</span>
                  <span v-if="listing.is_featured" class="badge bg-warning text-dark"><i class="mdi mdi-star me-1"></i>Featured</span>
                </div>
              </div>
              <!-- Thumbnails -->
              <div class="ld-thumbnails" v-if="photos.length > 1">
                <div v-for="(photo, pi) in photos" :key="'th-'+pi"
                     class="ld-thumb" :class="{ active: activePhotoIndex === pi }"
                     @click="activePhotoIndex = pi">
                  <img :src="photoUrl(photo.photo_url || photo)" :alt="'Photo ' + (pi+1)" />
                </div>
              </div>
            </div>

            <!-- Listing Title & Quick Info (mobile) -->
            <div class="d-lg-none mb-4">
              <h3 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">{{ listing.title }}</h3>
              <div class="ld-price-big mb-2">KES {{ formatPrice(listing.price) }}</div>
              <div class="d-flex align-items-center flex-wrap mb-2" style="gap:6px;">
                <span v-if="listing.is_verified" class="badge bg-success bg-opacity-25 text-success"><i class="mdi mdi-check-decagram"></i> Verified</span>
                <span v-if="listing.is_featured" class="badge bg-warning bg-opacity-25 text-warning"><i class="mdi mdi-star"></i> Featured</span>
                <span class="badge bg-dark" style="border:1px solid var(--fm-br,#2A2520);" v-if="listing.listing_type">{{ listing.listing_type }}</span>
              </div>
            </div>

            <!-- Animal Details -->
            <div class="ld-section">
              <h5 class="ld-section-title"><i class="mdi mdi-information me-2"></i>Animal Details</h5>
              <div class="row g-3">
                <div class="col-6 col-md-4" v-if="listing.species_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Species</div>
                    <div class="ld-detail-value"><i :class="getSpeciesIcon(listing.species_name)" class="me-1"></i> {{ listing.species_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.breed_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Breed</div>
                    <div class="ld-detail-value">{{ listing.breed_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.sex">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Sex</div>
                    <div class="ld-detail-value"><i class="mdi mdi-gender-male-female me-1"></i> {{ listing.sex }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.weight">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Weight</div>
                    <div class="ld-detail-value"><i class="mdi mdi-weight-kilogram me-1"></i> {{ listing.weight }} kg</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.age_text || listing.age_months">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Age</div>
                    <div class="ld-detail-value"><i class="mdi mdi-clock-outline me-1"></i> {{ listing.age_text || listing.age_months + ' months' }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.quantity">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Quantity</div>
                    <div class="ld-detail-value"><i class="mdi mdi-numeric me-1"></i> {{ listing.quantity }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.listing_type">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Listing Type</div>
                    <div class="ld-detail-value">{{ listing.listing_type === 'batch' ? 'Batch' : 'Individual' }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.county_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Location</div>
                    <div class="ld-detail-value"><i class="mdi mdi-map-marker me-1"></i> {{ listing.county_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.created_at">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Listed</div>
                    <div class="ld-detail-value"><i class="mdi mdi-calendar me-1"></i> {{ formatDate(listing.created_at) }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.views_count">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Views</div>
                    <div class="ld-detail-value"><i class="mdi mdi-eye me-1"></i> {{ listing.views_count }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="ld-section" v-if="listing.description">
              <h5 class="ld-section-title"><i class="mdi mdi-text me-2"></i>Description</h5>
              <p class="ld-description">{{ listing.description }}</p>
            </div>

            <!-- Health Records -->
            <div class="ld-section" v-if="healthRecords.length > 0">
              <h5 class="ld-section-title"><i class="mdi mdi-heart-pulse me-2"></i>Health Records</h5>
              <div class="ld-health-list">
                <div class="ld-health-item" v-for="(hr, hi) in healthRecords" :key="'hr-'+hi">
                  <div class="ld-health-icon">
                    <i class="mdi mdi-needle" style="color:#BFA164;"></i>
                  </div>
                  <div>
                    <div class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ hr.vaccine_name }} <span v-if="hr.disease_target" style="color:var(--fm-m,#A09888);font-size:11px;">— {{ hr.disease_target }}</span></div>
                    <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ formatDate(hr.administered_date) }} <span v-if="hr.administered_by">• {{ hr.administered_by }}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tags / Keywords -->
            <div class="ld-section" v-if="listing.tags && listing.tags.length > 0">
              <h5 class="ld-section-title"><i class="mdi mdi-tag-multiple me-2"></i>Tags</h5>
              <div class="d-flex flex-wrap" style="gap:6px;">
                <span class="ld-tag" v-for="(tag, ti) in listing.tags" :key="'tag-'+ti">{{ tag }}</span>
              </div>
            </div>

          </div>

          <!-- ===== RIGHT: PRICE, SELLER, ACTIONS ===== -->
          <div class="col-lg-4">

            <!-- Price & Action Card -->
            <div class="ld-action-card">
              <!-- Title (desktop only) -->
              <div class="d-none d-lg-block">
                <h4 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">{{ listing.title }}</h4>
                <div class="d-flex align-items-center flex-wrap mb-3" style="gap:6px;">
                  <span v-if="listing.is_verified" class="badge bg-success bg-opacity-25 text-success"><i class="mdi mdi-check-decagram"></i> Verified</span>
                  <span v-if="listing.is_featured" class="badge bg-warning bg-opacity-25 text-warning"><i class="mdi mdi-star"></i> Featured</span>
                  <span class="badge bg-dark" style="border:1px solid var(--fm-br,#2A2520);" v-if="listing.listing_type">{{ listing.listing_type === 'batch' ? 'Batch' : 'Individual' }}</span>
                </div>
              </div>

              <!-- Price -->
              <div class="ld-price-section">
                <div class="ld-price-big">KES {{ formatPrice(listing.price) }}</div>
                <div v-if="listing.price_per_unit" class="text-muted" style="font-size:13px;">
                  KES {{ formatPrice(listing.price_per_unit) }} per {{ listing.unit || 'kg' }}
                </div>
                <div v-if="listing.is_negotiable" class="mt-1">
                  <span class="badge bg-warning bg-opacity-10 text-warning" style="font-size:11px;"><i class="mdi mdi-handshake me-1"></i>Negotiable</span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex flex-column" style="gap:10px;">
                <button class="btn btn-warning text-dark fw-bold btn-lg w-100" @click="addToCart" :disabled="addingToCart">
                  <span v-if="addingToCart" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else :class="addedToCart ? 'mdi mdi-cart-check' : 'mdi mdi-cart-plus'" class="me-1"></i>
                  {{ addedToCart ? 'Added — view cart' : 'Add to Cart — KES ' + formatPrice(listing.price) }}
                </button>
                <button class="btn btn-outline-warning w-100" @click="makeOffer">
                  <i class="mdi mdi-handshake me-1"></i> Make an Offer
                </button>
                <button class="btn btn-outline-warning w-100" @click="messageSeller">
                  <i class="mdi mdi-message-text me-1"></i> Message Seller
                </button>
                <button v-if="seller.phone" class="btn btn-outline-secondary w-100 mt-2" @click="callSeller">
                  <i class="mdi mdi-phone me-1"></i> Call Seller
                </button>
                <div class="d-flex" style="gap:8px;">
                  <button class="btn btn-sm flex-fill" :class="listing.is_saved ? 'btn-danger' : 'btn-outline-secondary'" @click="toggleSave">
                    <i :class="listing.is_saved ? 'mdi mdi-heart' : 'mdi mdi-heart-outline'" class="me-1"></i>
                    {{ listing.is_saved ? 'Saved' : 'Save' }}
                  </button>
                  <button class="btn btn-sm btn-outline-secondary flex-fill" @click="shareListing">
                    <i class="mdi mdi-share-variant me-1"></i> Share
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" @click="reportListing">
                    <i class="mdi mdi-flag"></i>
                  </button>
                </div>
              </div>

              <!-- Escrow Trust Badge -->
              <div class="ld-escrow-badge">
                <i class="mdi mdi-shield-lock" style="font-size:20px;color:#34c38f;"></i>
                <div>
                  <div class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:13px;">M-Pesa Escrow Protected</div>
                  <div style="color:var(--fm-m,#A09888);font-size:11px;">Pay safely. Funds held until you confirm delivery.</div>
                </div>
              </div>
            </div>

            <!-- Seller Card -->
            <div class="ld-seller-card">
              <div class="d-flex align-items-center mb-3">
                <div class="ld-seller-avatar-lg">
                  <img v-if="seller.photo_url" :src="photoUrl(seller.photo_url)" :alt="seller.name" class="ld-seller-photo" />
                  <span v-else>{{ getInitials(seller.name || seller.farm_name) }}</span>
                </div>
                <div class="ms-3">
                  <h6 class="fw-bold mb-0" style="color:var(--fm-h,#F5F0E8);">{{ seller.name || seller.farm_name || 'Seller' }}</h6>
                  <div v-if="seller.farm_name && seller.name" style="color:var(--fm-m,#A09888);font-size:12px;">{{ seller.farm_name }}</div>
                  <div class="d-flex align-items-center mt-1" style="gap:8px;">
                  <span v-if="seller.rating" style="color:#f1b44c;font-size:13px;">
                    <i class="mdi mdi-star"></i> {{ seller.rating }}
                  </span>
                    <span v-if="seller.total_reviews" style="color:var(--fm-m,#A09888);font-size:12px;">
                    ({{ seller.total_reviews }} reviews)
                  </span>
                    <span v-if="seller.is_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;"><i class="mdi mdi-check-decagram"></i> Verified</span>
                  </div>
                </div>
              </div>

              <!-- Seller Stats -->
              <div class="ld-seller-stats">
                <div class="ld-seller-stat" v-if="seller.total_listings">
                  <div class="ld-stat-value">{{ seller.total_listings }}</div>
                  <div class="ld-stat-label">Listings</div>
                </div>
                <div class="ld-seller-stat" v-if="seller.total_sold">
                  <div class="ld-stat-value">{{ seller.total_sold }}</div>
                  <div class="ld-stat-label">Sold</div>
                </div>
                <div class="ld-seller-stat" v-if="seller.member_since">
                  <div class="ld-stat-value">{{ seller.member_since }}</div>
                  <div class="ld-stat-label">Member Since</div>
                </div>
                <div class="ld-seller-stat" v-if="seller.response_time">
                  <div class="ld-stat-value">{{ seller.response_time }}</div>
                  <div class="ld-stat-label">Avg Response</div>
                </div>
              </div>

              <div v-if="seller.county_name" class="mb-3" style="color:var(--fm-m,#A09888);font-size:13px;">
                <i class="mdi mdi-map-marker me-1"></i> {{ seller.county_name }}
              </div>

              <a href="javascript:void(0)" @click="$router.push('/marketplace/seller/' + (seller.id || listing.seller_id))" class="btn btn-outline-warning w-100 btn-sm">
                <i class="mdi mdi-account me-1"></i> View Full Profile
              </a>
            </div>

            <!-- Delivery Info -->
            <div class="ld-info-card" v-if="listing.delivery_options">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);font-size:14px;"><i class="mdi mdi-truck-delivery me-2"></i>Delivery</h6>
              <div v-if="listing.delivery_options.pickup" class="ld-delivery-option">
                <i class="mdi mdi-map-marker-radius text-success"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Pickup Available</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ listing.delivery_options.pickup_location || listing.county_name }}</div>
                </div>
              </div>
              <div v-if="listing.delivery_options.delivery" class="ld-delivery-option">
                <i class="mdi mdi-truck text-warning"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Delivery Available</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ listing.delivery_options.delivery_info || 'Fees may apply' }}</div>
                </div>
              </div>
              <div v-if="listing.delivery_options.flockr_transport" class="ld-delivery-option">
                <i class="mdi mdi-truck-fast" style="color:#BFA164;"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Flockr Transport</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;">GPS-tracked, welfare-certified delivery</div>
                </div>
              </div>
            </div>

            <!-- Safety Tips -->
            <div class="ld-info-card">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);font-size:14px;"><i class="mdi mdi-shield-alert me-2 text-warning"></i>Safety Tips</h6>
              <ul class="ld-tips-list">
                <li>Always use Flockr escrow for payment protection</li>
                <li>Inspect the animal before confirming delivery</li>
                <li>Check seller ratings and reviews</li>
                <li>Request health certificates and records</li>
              </ul>
            </div>

            <!-- Payment Disclaimer -->
            <div style="background:rgba(255,107,107,0.06);border:1px solid rgba(255,107,107,0.2);border-radius:10px;padding:14px;margin-top:12px;">
              <div style="display:flex;align-items:flex-start;gap:10px;">
                <i class="mdi mdi-alert-circle" style="color:#FF6B6B;font-size:20px;flex-shrink:0;margin-top:1px;"></i>
                <div>
                  <div style="color:#FF6B6B;font-size:13px;font-weight:700;margin-bottom:4px;">Do NOT pay the seller directly</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;line-height:1.5;">All payments must be made through the Flockr platform. Direct payments to sellers are not protected and Flockr cannot assist with disputes for transactions made outside the system.</div>
                </div>
              </div>
            </div>

            <!-- Terms -->
            <div style="text-align:center;padding:12px 0 0;margin-top:12px;">
              <p style="color:#4A4540;font-size:11px;line-height:1.5;margin:0;">By using Flockr, you agree to our <a href="javascript:void(0)" style="color:#BFA164;text-decoration:underline;">Terms &amp; Conditions</a> and <a href="javascript:void(0)" style="color:#BFA164;text-decoration:underline;">Privacy Policy</a>.</p>
            </div>

          </div>
        </div>

        <!-- ========== RELATED LISTINGS ========== -->
        <section class="mt-5 pt-4" style="border-top:1px solid var(--fm-br);" v-if="relatedListings.length > 0">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="fw-bold mb-0" style="color:var(--fm-h,#F5F0E8);">Similar Listings</h5>
            <a href="javascript:void(0)" @click="$router.push('/marketplace/search?species=' + (listing.species_id || ''))" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See More <i class="mdi mdi-arrow-right"></i></a>
          </div>
          <div class="row g-3">
            <div class="col-lg-3 col-md-4 col-6" v-for="(rl, rli) in relatedListings" :key="'rl-'+rli">
              <div class="ld-related-card" @click="goToListing(rl.id)">
                <div class="ld-related-img">
                  <img v-if="rl.primary_photo" :src="photoUrl(rl.primary_photo)" :alt="rl.title" style="width:100%;height:100%;object-fit:cover;" />
                  <i v-else :class="getSpeciesIcon(rl.species_name)" style="font-size:40px;color:rgba(191,161,100,0.3);"></i>
                </div>
                <div class="p-3">
                  <h6 class="fw-bold mb-1" style="color:var(--fm-h,#F5F0E8);font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ rl.title }}</h6>
                  <div style="color:var(--fm-m,#A09888);font-size:11px;" class="mb-1"><i class="mdi mdi-map-marker"></i> {{ rl.county_name || rl.location }}</div>
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="fw-bold" style="color:#BFA164;font-size:15px;">KES {{ formatPrice(rl.price) }}</div>
                    <div class="d-flex align-items-center" style="gap:3px;font-size:11px;color:var(--fm-m,#A09888);">
                      <div class="ld-mini-avatar">{{ getInitials(rl.seller_name) }}</div>
                      <span>{{ rl.seller_name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      <!-- ========== MOBILE BOTTOM BAR ========== -->
      <div class="ld-mobile-bar d-lg-none" v-if="listing.id && !loading">
        <div class="ld-mobile-price">
          <div class="fw-bold" style="color:#BFA164;font-size:18px;">KES {{ formatPrice(listing.price) }}</div>
          <div style="color:var(--fm-m,#A09888);font-size:11px;" v-if="listing.is_negotiable">Negotiable</div>
        </div>
        <div class="d-flex" style="gap:8px;">
          <button class="btn btn-outline-warning btn-sm" @click="messageSeller"><i class="mdi mdi-message-text"></i></button>
          <button class="btn btn-warning text-dark fw-bold btn-sm px-3" @click="addToCart" :disabled="addingToCart">
            <i :class="addedToCart ? 'mdi mdi-cart-check' : 'mdi mdi-cart-plus'" class="me-1"></i>
            {{ addedToCart ? 'In cart' : 'Add to Cart' }}
          </button>
          <button class="btn btn-outline-warning btn-sm px-3" @click="makeOffer">
            <i class="mdi mdi-handshake me-1"></i> Offer
          </button>
        </div>
      </div>

      <div class="rightbar-overlay"></div>
    </div>
  </div>
</template>

<script>
import { execute, executeJson, flocker, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

export default {
  name: "ListingDetail",
  components: { UserAvatar },

  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,
      myAvatar: sessionStorage.getItem('userAvatar') || '',
      myName: sessionStorage.getItem('userName') || '',

      listing: {},
      seller: {},
      photos: [],
      healthRecords: [],
      relatedListings: [],
      activePhotoIndex: 0,
      loading: false,
      addingToCart: false,
      addedToCart: false
    };
  },

  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    activePhoto() {
      if (this.photos.length === 0) return null;
      var photo = this.photos[this.activePhotoIndex];
      return photo ? this.photoUrl(photo.photo_url || photo) : null;
    }
  },

  watch: {
    '$route.params.id': function(newId) {
      if (newId) {
        this.loadListing(newId);
      }
    }
  },

  mounted() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var listingId = this.$route.params.id;
    if (listingId) {
      this.loadListing(listingId);
    }
  },

  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  methods: {
    syncTheme() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      var el = this.$el;
      if (el) {
        el.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        el.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},


    async loadListing(id) {
      this.loading = true;
      this.listing = {};
      this.seller = {};
      this.photos = [];
      this.healthRecords = [];
      this.relatedListings = [];
      this.activePhotoIndex = 0;
      window.scrollTo({ top: 0 });

      try {
        await this.fetchListingDetail(id);
        if (this.listing.id) {
          this.fetchSellerInfo();
          this.fetchRelatedListings();
        }
      } catch (e) {
        console.error('Error loading listing:', e);
      } finally {
        this.loading = false;
      }
    },

    // ============ API CALLS ============

    async fetchListingDetail(id) {
      var viewerId = sessionStorage.getItem('userId') || '';
      try {
        var response = await executeJson({
          function: "getListing",
          listingId: id,
          viewerId: viewerId
        }, flocker);
        if (response.data.success) {
          this.listing = response.data.data.listing || {};
          this.photos = this.listing.photos || [];
          if (this.photos.length === 0 && this.listing.photo_url) {
            this.photos = [{ photo_url: this.listing.photo_url }];
          }
          this.healthRecords = this.listing.vaccinations || [];
        }
      } catch (e) {
        console.error('Error fetching listing detail:', e);
      }
    },

    async fetchSellerInfo() {
      var sellerId = this.listing.seller_id;
      if (!sellerId) return;
      try {
        var response = await executeJson({
          function: "getUserProfile",
          userId: sellerId
        }, flocker);
        if (response.data.success) {
          var user = response.data.data.user || {};
          var ratingStats = (user.rating_stats || []).find(function(r) { return r.role_context === 'seller'; }) || {};
          var isVerified = (user.verifications || []).some(function(v) { return v.verification_status === 'verified'; });
          this.seller = {
            id: user.id,
            name: user.display_name || (user.first_name + ' ' + user.last_name),
            farm_name: '',
            phone: user.phone,
            photo_url: user.avatar_url,
            county_name: user.county_name,
            rating: ratingStats.avg_overall || null,
            total_reviews: ratingStats.total_reviews || 0,
            is_verified: isVerified,
            total_listings: 0,
            total_sold: 0,
            member_since: user.created_at ? new Date(user.created_at).getFullYear().toString() : '',
            response_time: ''
          };
        }
      } catch (e) {
        console.error('Error fetching seller info:', e);
      }
    },

    async fetchRelatedListings() {
      try {
        var response = await executeJson({
          function: "searchListings",
          speciesId: this.listing.species_id || '',
          limit: 4,
          page: 1,
          sortBy: "popular"
        }, flocker);
        if (response.data.success) {
          this.relatedListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching related listings:', e);
      }
    },

    async toggleSave() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      try {
        var response = await executeJson({
          function: "saveListing",
          userId: userId,
          listingId: this.listing.id
        }, flocker);
        if (response.data.success) {
          this.listing.is_saved = !this.listing.is_saved;
        }
      } catch (e) {
        console.error('Error toggling save:', e);
      }
    },

    // ============ ACTIONS ============

    async addToCart() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }
      // If already added in this session, second click goes to cart
      if (this.addedToCart) { this.$router.push('/cart'); return; }
      this.addingToCart = true;
      try {
        var r = await executeJson({ function: 'addToCart', userId: userId, listingId: this.listing.id, quantity: 1 }, flocker);
        if (r.data && r.data.success) {
          this.addedToCart = true;
          // Bubble new count to topbar listeners + persist for next page load
          var count = (r.data.data && r.data.data.cartCount) || 0;
          sessionStorage.setItem('flockr_cart_count', count);
          window.dispatchEvent(new CustomEvent('flockr-cart-updated', { detail: { count: count } }));
        } else {
          alert((r.data && r.data.message) || 'Could not add to cart');
        }
      } catch (e) {
        alert('Network error');
      }
      this.addingToCart = false;
    },
    makeOffer() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      this.$router.push('/marketplace/offer/' + this.listing.id);
    },

    async messageSeller() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }
      var sellerId = this.listing.seller_id;
      if (!sellerId || sellerId === 'undefined') { alert('Seller info not available.'); return; }
      var photo = '';
      if (this.photos.length > 0 && this.photos[0].photo_url) photo = this.photos[0].photo_url;
      else if (this.listing.photo_url) photo = this.listing.photo_url;
      var cardData = JSON.stringify({
        _type: 'listing_card',
        listing_id: this.listing.id,
        title: this.listing.title || '',
        price: this.listing.price || 0,
        photo: photo,
        species: this.listing.species_name || '',
        breed: this.listing.breed_name || '',
        text: 'Hi, I am interested in this listing. Is it still available?'
      });
      try {
        var f = new FormData();
        f.append('function', 'sendMessage');
        f.append('senderId', userId);
        f.append('receiverId', sellerId);
        f.append('content', cardData);
        f.append('messageType', 'listing_card');
        f.append('contextType', 'listing');
        f.append('contextId', this.listing.id);
        f.append('listingId', this.listing.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data.conversationId) {
          this.$router.push('/messages/' + r.data.data.conversationId);
        } else { this.$router.push('/messages'); }
      } catch (e) { this.$router.push('/messages'); }
    },
    callSeller() {
      if (this.seller.phone) { window.open('tel:' + this.seller.phone); }
    },

    shareListing() {
      var url = window.location.href;
      if (navigator.share) {
        navigator.share({
          title: this.listing.title,
          text: 'Check out this listing on Flockr: ' + this.listing.title + ' - KES ' + this.formatPrice(this.listing.price),
          url: url
        }).catch(function() {});
      } else {
        navigator.clipboard.writeText(url).then(function() {
          alert('Link copied to clipboard!');
        }).catch(function() {
          prompt('Copy this link:', url);
        });
      }
    },

    reportListing() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      var reason = prompt('Why are you reporting this listing?');
      if (reason) {
        executeJson({
          function: "submitReport",
          reporterId: userId,
          reportedType: "listing",
          reportedId: this.listing.id,
          reason: reason
        }, flocker).then(function() {
          alert('Report submitted. Thank you.');
        }).catch(function() {
          alert('Failed to submit report. Please try again.');
        });
      }
    },

    goToListing(id) {
      this.$router.push('/marketplace/listing/' + id);
    },

    // ============ PHOTO NAVIGATION ============

    prevPhoto() {
      if (this.activePhotoIndex > 0) {
        this.activePhotoIndex--;
      } else {
        this.activePhotoIndex = this.photos.length - 1;
      }
    },

    nextPhoto() {
      if (this.activePhotoIndex < this.photos.length - 1) {
        this.activePhotoIndex++;
      } else {
        this.activePhotoIndex = 0;
      }
    },

    // ============ HELPERS ============

    formatPrice(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-KE');
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    getInitials(name) {
      if (!name) return '?';
      var parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    getSpeciesIcon(speciesName) {
      if (!speciesName) return 'mdi mdi-paw';
      var s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'mdi mdi-cow';
      if (s.includes('goat')) return 'mdi mdi-alpha-g-circle';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'mdi mdi-sheep';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'mdi mdi-bird';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'mdi mdi-pig';
      if (s.includes('camel')) return 'mdi mdi-alpha-c-circle';
      return 'mdi mdi-paw';
    },

    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    }
  }
};
</script>

<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style>
html[data-theme="light"] body {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}
html[data-theme="dark"] body {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}
html[data-theme="light"] .listing-detail-root,
html[data-theme="light"] .ld-layout-wrapper,
html[data-theme="light"] #layout-wrapper.ld-layout-wrapper {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}
html[data-theme="dark"] .listing-detail-root,
html[data-theme="dark"] .ld-layout-wrapper,
html[data-theme="dark"] #layout-wrapper.ld-layout-wrapper {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}

html[data-theme="light"] .listing-detail-root nav.mb-3,
html[data-theme="light"] .listing-detail-root nav.mb-3 ol,
html[data-theme="light"] .listing-detail-root .breadcrumb {
  background:#F7F5F2!important;
}

</style>

<style scoped>
.listing-detail-root, .ld-layout-wrapper { background:var(--fm-bg2,#0F0F0F)!important; min-height:100vh; color:var(--fm-h,#F5F0E8)!important; }

/* TOP BAR */
.ld-topbar { background:var(--fm-bg1,#141414)!important; border-bottom:1px solid var(--fm-br,#2A2520); padding:10px 0; position:sticky; top:0; z-index:1040; }
.ld-nav-link { color:var(--fm-m,#A09888)!important; text-decoration:none; font-size:14px; font-weight:500; }
.ld-nav-link:hover { color:#BFA164!important; }

/* BREADCRUMB */
.ld-breadcrumb { background:transparent; padding:0; margin:0; font-size:13px; }
.ld-breadcrumb .breadcrumb-item a { color:var(--fm-m,#A09888)!important; text-decoration:none; }
.ld-breadcrumb .breadcrumb-item a:hover { color:#BFA164!important; }
.ld-breadcrumb .breadcrumb-item.active { color:var(--fm-m,#A09888)!important; }
.ld-breadcrumb .breadcrumb-item+.breadcrumb-item::before { color:var(--fm-m,#A09888)!important; }


/* BREADCRUMB */
.ld-breadcrumb,
.ld-breadcrumb.breadcrumb {
  background:transparent!important;
  padding:0!important;
  margin:0!important;
  font-size:13px;
  box-shadow:none!important;
  border:none!important;
}
.ld-breadcrumb .breadcrumb-item a {
  color:var(--fm-m,#A09888)!important;
  text-decoration:none;
}
.ld-breadcrumb .breadcrumb-item a:hover {
  color:#BFA164!important;
}
.ld-breadcrumb .breadcrumb-item.active {
  color:var(--fm-m,#A09888)!important;
}
.ld-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
  color:var(--fm-m,#A09888)!important;
}
html[data-theme="light"] .ld-breadcrumb,
html[data-theme="light"] .ld-breadcrumb.breadcrumb {
  background:#F7F5F2!important;
}
html[data-theme="dark"] .ld-breadcrumb,
html[data-theme="dark"] .ld-breadcrumb.breadcrumb {
  background:transparent!important;
}

/* PHOTO GALLERY */
.ld-gallery { border-radius:16px; overflow:hidden; background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); }
.ld-main-photo { position:relative; width:100%; height:400px; background:linear-gradient(135deg,#1A1510,#0F0F0F); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.ld-main-img { width:100%; height:100%; object-fit:cover; }
.ld-main-placeholder { display:flex; flex-direction:column; align-items:center; justify-content:center; }
.ld-photo-nav { position:absolute; top:50%; transform:translateY(-50%); width:40px; height:40px; border-radius:50%; background:rgba(0,0,0,.6)!important; border:none; color:#fff!important; font-size:22px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .2s; }
.ld-photo-nav:hover { background:rgba(0,0,0,.8)!important; }
.ld-photo-prev { left:12px; }
.ld-photo-next { right:12px; }
.ld-photo-counter { position:absolute; bottom:12px; right:12px; background:rgba(0,0,0,.6)!important; color:#fff!important; padding:4px 12px; border-radius:50px; font-size:12px; font-weight:600; }
.ld-photo-badges { position:absolute; top:12px; left:12px; display:flex; gap:6px; }
.ld-thumbnails { display:flex; gap:8px; padding:12px; overflow-x:auto; }
.ld-thumb { width:72px; height:56px; border-radius:8px; overflow:hidden; border:2px solid transparent; cursor:pointer; flex-shrink:0; opacity:0.6; transition:all .2s; }
.ld-thumb.active { border-color:#BFA164; opacity:1; }
.ld-thumb:hover { opacity:1; }
.ld-thumb img { width:100%; height:100%; object-fit:cover; }

/* SECTIONS */
.ld-section { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; padding:20px; margin-bottom:16px; }
.ld-section-title { color:var(--fm-h,#F5F0E8)!important; font-size:16px; font-weight:700; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid var(--fm-br,#2A2520); }

/* DETAIL ITEMS */
.ld-detail-item { background:var(--fm-bg2,#0F0F0F)!important; border-radius:10px; padding:12px; }
.ld-detail-label { color:var(--fm-m,#A09888)!important; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:4px; }
.ld-detail-value { color:var(--fm-h,#F5F0E8)!important; font-size:14px; font-weight:600; }

/* DESCRIPTION */
.ld-description { color:var(--fm-m,#A09888)!important; font-size:14px; line-height:1.8; white-space:pre-line; }

/* HEALTH RECORDS */
.ld-health-list { display:flex; flex-direction:column; gap:10px; }
.ld-health-item { display:flex; align-items:center; gap:12px; background:var(--fm-bg2,#0F0F0F)!important; border-radius:10px; padding:10px 14px; }
.ld-health-icon { width:36px; height:36px; background:rgba(191,161,100,.1)!important; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* TAGS */
.ld-tag { background:rgba(191,161,100,.08)!important; color:var(--fm-m,#A09888)!important; padding:4px 12px; border-radius:50px; font-size:12px; border:1px solid var(--fm-br,#2A2520); }

/* ACTION CARD */
.ld-action-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:16px; padding:24px; margin-bottom:16px; position:sticky; top:70px; }
.ld-price-section { margin-bottom:20px; padding-bottom:16px; border-bottom:1px solid var(--fm-br,#2A2520); }
.ld-price-big { font-size:28px; font-weight:800; color:#BFA164!important; }
.ld-escrow-badge { display:flex; align-items:center; gap:12px; background:rgba(52,195,143,.06)!important; border:1px solid rgba(52,195,143,.15); border-radius:10px; padding:12px; margin-top:16px; }

/* SELLER CARD */
.ld-seller-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:16px; padding:20px; margin-bottom:16px; }
.ld-seller-avatar-lg { width:56px; height:56px; background:#BFA164!important; color:#0A0A0A!important; border-radius:50%; font-size:20px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
.ld-seller-photo { width:100%; height:100%; object-fit:cover; }
.ld-seller-stats { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:12px; }
.ld-seller-stat { background:var(--fm-bg2,#0F0F0F)!important; border-radius:8px; padding:10px; text-align:center; }
.ld-stat-value { color:var(--fm-h,#F5F0E8)!important; font-size:16px; font-weight:700; }
.ld-stat-label { color:var(--fm-m,#A09888)!important; font-size:11px; }

/* INFO CARDS */
.ld-info-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:16px; padding:20px; margin-bottom:16px; }
.ld-delivery-option { display:flex; align-items:center; gap:12px; padding:8px 0; font-size:14px; }
.ld-delivery-option i { font-size:20px; }
.ld-tips-list { padding-left:18px; margin:0; }
.ld-tips-list li { color:var(--fm-m,#A09888)!important; font-size:13px; margin-bottom:6px; }

/* RELATED LISTINGS */
.ld-related-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; overflow:hidden; cursor:pointer; transition:all .3s; height:100%; }
.ld-related-card:hover { border-color:#444; transform:translateY(-3px); }
.ld-related-img { height:120px; background:linear-gradient(135deg,#1A1510,#0F0F0F); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.ld-mini-avatar { width:16px; height:16px; background:#BFA164!important; color:#0A0A0A!important; border-radius:50%; font-size:7px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* MOBILE BOTTOM BAR */
.ld-mobile-bar { position:fixed; bottom:0; left:0; right:0; background:var(--fm-bg1,#141414)!important; border-top:1px solid var(--fm-br,#2A2520); padding:12px 16px; display:flex; justify-content:space-between; align-items:center; z-index:1040; }
.ld-mobile-price { }

/* RESPONSIVE */
@media (max-width:991px) {
  .ld-main-photo { height:300px; }
  .ld-action-card { position:static; }
}
@media (max-width:575px) {
  .ld-main-photo { height:220px; }
  .ld-detail-item { padding:8px; }
  .ld-related-img { height:100px; }
}
</style>
